import React, { useEffect, useRef, useState, useMemo } from "react";
import "./TutorialVideoButton.css";

/* ─────────────────────────────────────────────────────────────
   Tutorial Video Library
   Drop new entries here. `src` can be a public-folder path
   ("/videos/booking.mp4"), a CDN URL, or YouTube embed URL
   ("https://www.youtube.com/embed/XXXX").
   ───────────────────────────────────────────────────────────── */
const TUTORIAL_VIDEOS = [
  {
    id: "logging-in",
    title: "Logging In",
    description: "Sign in to your iCareVillage account and access your dashboard.",
    duration: "",
    category: "Getting Started",
    src: "/videos/Logging In.mp4",
    poster: "",
  },
  {
    id: "adding-family-members",
    title: "Adding Family Members",
    description: "Add and manage family members under your account profile.",
    duration: "",
    category: "Account",
    src: "/videos/Adding Family Members.mp4",
    poster: "",
  },
  {
    id: "making-appointments",
    title: "Making Appointments",
    description: "Book a care service and request a provider for chosen dates.",
    duration: "",
    category: "Bookings",
    src: "/videos/Making Appointments.mp4",
    poster: "",
  },
];

/* SVG icons (consistent stroke) */
const I = ({ name, size = 18 }) => {
  const p = {
    width: size,
    height: size,
    viewBox: "0 0 24 24",
    fill: "none",
    stroke: "currentColor",
    strokeWidth: 1.75,
    strokeLinecap: "round",
    strokeLinejoin: "round",
    "aria-hidden": "true",
  };
  switch (name) {
    case "play":
      return (
        <svg {...p}>
          <path d="M6 4l14 8-14 8V4z" fill="currentColor" stroke="none" />
        </svg>
      );
    case "playCircle":
      return (
        <svg {...p}>
          <circle cx="12" cy="12" r="10" />
          <path d="M10 8l6 4-6 4V8z" fill="currentColor" stroke="none" />
        </svg>
      );
    case "videoLib":
      return (
        <svg {...p}>
          <rect x="3" y="6" width="13" height="12" rx="2" />
          <path d="M16 10l5-3v10l-5-3z" fill="currentColor" stroke="none" />
        </svg>
      );
    case "x":
      return (
        <svg {...p}>
          <path d="M18 6L6 18M6 6l12 12" />
        </svg>
      );
    case "clock":
      return (
        <svg {...p}>
          <circle cx="12" cy="12" r="9" />
          <path d="M12 7v5l3 2" />
        </svg>
      );
    case "search":
      return (
        <svg {...p}>
          <circle cx="11" cy="11" r="7" />
          <path d="M21 21l-4.3-4.3" />
        </svg>
      );
    case "check":
      return (
        <svg {...p}>
          <path d="M5 12l5 5L20 7" />
        </svg>
      );
    default:
      return null;
  }
};

const isYouTube = (src = "") =>
  /(?:youtube\.com|youtu\.be)/.test(src);

/* Encode spaces / unicode in local file paths without re-encoding ":" "/" "?" */
const safeSrc = (src = "") => {
  if (!src) return src;
  if (/^https?:\/\//i.test(src)) return src;
  try {
    return src.split("/").map(encodeURIComponent).join("/");
  } catch {
    return src;
  }
};

const formatDuration = (sec) => {
  if (!sec || !isFinite(sec)) return "";
  const m = Math.floor(sec / 60);
  const s = Math.floor(sec % 60);
  return `${m}:${String(s).padStart(2, "0")}`;
};

const TutorialVideoButton = () => {
  const [open, setOpen] = useState(false);
  const [activeId, setActiveId] = useState(TUTORIAL_VIDEOS[0]?.id);
  const [query, setQuery] = useState("");
  const [watched, setWatched] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem("@uphealth_tutorial_watched") || "[]");
    } catch {
      return [];
    }
  });
  const [durations, setDurations] = useState({});
  const videoRef = useRef(null);
  const closeBtnRef = useRef(null);

  /* Probe duration for any local mp4s we don't know yet */
  useEffect(() => {
    if (!open) return;
    TUTORIAL_VIDEOS.forEach((v) => {
      if (durations[v.id] || v.duration || isYouTube(v.src)) return;
      const probe = document.createElement("video");
      probe.preload = "metadata";
      probe.src = safeSrc(v.src);
      const onMeta = () => {
        setDurations((d) => ({ ...d, [v.id]: formatDuration(probe.duration) }));
        probe.removeEventListener("loadedmetadata", onMeta);
      };
      probe.addEventListener("loadedmetadata", onMeta);
    });
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const active = useMemo(
    () => TUTORIAL_VIDEOS.find((v) => v.id === activeId) || TUTORIAL_VIDEOS[0],
    [activeId]
  );

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return TUTORIAL_VIDEOS;
    return TUTORIAL_VIDEOS.filter(
      (v) =>
        v.title.toLowerCase().includes(q) ||
        v.description.toLowerCase().includes(q) ||
        v.category.toLowerCase().includes(q)
    );
  }, [query]);

  /* Body scroll lock + Esc close + focus management */
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("keydown", onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    setTimeout(() => closeBtnRef.current?.focus(), 60);
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [open]);

  /* Pause + reset video when switching or closing */
  useEffect(() => {
    if (!videoRef.current) return;
    try {
      videoRef.current.pause();
      videoRef.current.currentTime = 0;
    } catch {
      /* noop */
    }
  }, [activeId, open]);

  const markWatched = (id) => {
    if (watched.includes(id)) return;
    const next = [...watched, id];
    setWatched(next);
    try {
      localStorage.setItem("@uphealth_tutorial_watched", JSON.stringify(next));
    } catch {
      /* noop */
    }
  };

  return (
    <>
      {/* Floating Action Button */}
      <button
        type="button"
        className={`tvb-fab ${open ? "tvb-fab--hidden" : ""}`}
        onClick={() => setOpen(true)}
        aria-label="Open video tutorials"
        title="Watch tutorials"
      >
        <span className="tvb-fab-pulse" aria-hidden="true" />
        <span className="tvb-fab-icon" aria-hidden="true">
          <I name="playCircle" size={22} />
        </span>
        <span className="tvb-fab-label">Tutorials</span>
      </button>

      {/* Modal */}
      {open && (
        <div
          className="tvb-overlay"
          role="presentation"
          onClick={() => setOpen(false)}
        >
          <div
            className="tvb-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="tvb-title"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="tvb-header">
              <div className="tvb-header-left">
                <div className="tvb-header-icon" aria-hidden="true">
                  <I name="videoLib" size={20} />
                </div>
                <div>
                  <h2 id="tvb-title" className="tvb-title">
                    Video Tutorials
                  </h2>
                  <p className="tvb-subtitle">
                    Short walkthroughs of every feature.
                  </p>
                </div>
              </div>
              <button
                ref={closeBtnRef}
                type="button"
                className="tvb-close"
                onClick={() => setOpen(false)}
                aria-label="Close tutorials"
              >
                <I name="x" size={18} />
              </button>
            </div>

            <div className="tvb-body">
              {/* Playlist */}
              <aside className="tvb-playlist" aria-label="Tutorial list">
                <div className="tvb-search">
                  <I name="search" size={15} />
                  <input
                    type="text"
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    placeholder="Search tutorials…"
                    aria-label="Search tutorials"
                  />
                </div>
                <ul className="tvb-list">
                  {filtered.length === 0 && (
                    <li className="tvb-empty">No tutorials match.</li>
                  )}
                  {filtered.map((v) => {
                    const isActive = v.id === active?.id;
                    const isWatched = watched.includes(v.id);
                    const dur = v.duration || durations[v.id] || "";
                    return (
                      <li key={v.id}>
                        <button
                          type="button"
                          className={`tvb-list-item ${
                            isActive ? "tvb-list-item--active" : ""
                          }`}
                          onClick={() => setActiveId(v.id)}
                          aria-current={isActive ? "true" : undefined}
                        >
                          <span className="tvb-list-thumb" aria-hidden="true">
                            <I name="play" size={14} />
                          </span>
                          <span className="tvb-list-text">
                            <span className="tvb-list-title">
                              {v.title}
                              {isWatched && (
                                <span
                                  className="tvb-watched"
                                  title="Watched"
                                  aria-label="Watched"
                                >
                                  <I name="check" size={12} />
                                </span>
                              )}
                            </span>
                            <span className="tvb-list-meta">
                              <span className="tvb-tag">{v.category}</span>
                              {dur && (
                                <>
                                  <span className="tvb-dot">·</span>
                                  <span className="tvb-duration">
                                    <I name="clock" size={11} />
                                    {dur}
                                  </span>
                                </>
                              )}
                            </span>
                          </span>
                        </button>
                      </li>
                    );
                  })}
                </ul>
              </aside>

              {/* Player */}
              <section className="tvb-player" aria-label="Video player">
                {active ? (
                  <>
                    <div className="tvb-video-wrap">
                      {isYouTube(active.src) ? (
                        <iframe
                          key={active.id}
                          className="tvb-video"
                          src={active.src}
                          title={active.title}
                          frameBorder="0"
                          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                          allowFullScreen
                          onLoad={() => markWatched(active.id)}
                        />
                      ) : (
                        <video
                          ref={videoRef}
                          key={active.id}
                          className="tvb-video"
                          src={safeSrc(active.src)}
                          poster={active.poster ? safeSrc(active.poster) : undefined}
                          controls
                          playsInline
                          preload="metadata"
                          onPlay={() => markWatched(active.id)}
                          onLoadedMetadata={(e) =>
                            setDurations((d) => ({
                              ...d,
                              [active.id]: formatDuration(e.target.duration),
                            }))
                          }
                        >
                          Your browser does not support HTML5 video.
                        </video>
                      )}
                    </div>
                    <div className="tvb-info">
                      <h3 className="tvb-info-title">{active.title}</h3>
                      <p className="tvb-info-desc">{active.description}</p>
                      <div className="tvb-info-meta">
                        <span className="tvb-tag">{active.category}</span>
                        {(active.duration || durations[active.id]) && (
                          <span className="tvb-duration">
                            <I name="clock" size={12} />
                            {active.duration || durations[active.id]}
                          </span>
                        )}
                      </div>
                    </div>
                  </>
                ) : (
                  <div className="tvb-empty-state">
                    <I name="videoLib" size={32} />
                    <p>Select a tutorial to start watching.</p>
                  </div>
                )}
              </section>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default TutorialVideoButton;
